import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import { Sheet, ActionBar } from '../../components'
import BarcodeScanner, { isCameraAvailable } from '../../components/BarcodeScanner'
import { slotDisplayName } from '../photo-upload/PhotoUpload'
import { reopenPiece, resolvePiece } from './publishApi'
import { republishLine } from './RepublishNotice'

// The server's SKU rule (photoPipeline/reopen.js): 7 or 8 digits. Anything
// that is not all digits is a product handle or link, which the server
// resolves to its barcode (photoPipeline/resolvePiece.js).
const BARCODE = /^\d{7,8}$/
const canSubmit = input => (/^\d+$/.test(input) ? BARCODE.test(input) : input.length > 0)

// Why a handle or link named no piece, in the dialog's words; the server's
// sentence for anything else (a Shopify failure).
const RESOLVE_REFUSALS = ['notFound', 'noBarcode', 'severalBarcodes', 'notStoreBarcode', 'unreadable']
function resolveRefusalOf(response) {
    if (!RESOLVE_REFUSALS.includes(response.reason)) return { kind: 'error', detail: response.error }
    return { kind: 'unresolved', reason: response.reason, title: response.title, barcodes: (response.barcodes ?? []).join(', ') }
}

// What the reopen answered, as the dialog words it. The server's own sentence
// is kept for anything without a plainer one (being published, clean-up
// still running, no archived original, a Shopify or S3 failure).
function outcomeOf(sku, response) {
    if (response.alreadyReopened) return { kind: 'already', sku, republish: response.republish ?? { mode: response.mode } }
    if (!response.error) return { kind: 'reopened', sku }
    if (response.republishStarted) return { kind: 'started', sku, detail: response.error }
    if (Array.isArray(response.problems) && response.problems.length) return { kind: 'unmatched', sku, problems: response.problems }
    if (response.status === 409 && /not live/i.test(response.error)) return { kind: 'notLive', sku }
    return { kind: 'error', sku, detail: response.error }
}

// "Reopen piece": a published piece back to review by its barcode, typed,
// read by a hardware scanner (keystrokes ending in Enter) or by the camera.
export default function ReopenDialog({ open, fetchResource, onReopened, onClose }) {
    const { t, i18n } = useTranslation()
    const [barcode, setBarcode] = useState('')
    const [busy, setBusy] = useState(false)
    const [outcome, setOutcome] = useState(null)
    // The piece a handle or link resolved to, shown while it is reopened.
    const [piece, setPiece] = useState(null)
    const [showScanner, setShowScanner] = useState(false)

    async function submit(value) {
        const input = value.trim()
        if (busy || !canSubmit(input)) return
        setBusy(true)
        setOutcome(null)
        setPiece(null)
        let code = input
        if (!/^\d+$/.test(input)) {
            const resolved = await resolvePiece(fetchResource, input)
            if (resolved.error) {
                setBusy(false)
                setOutcome(resolveRefusalOf(resolved))
                return
            }
            code = resolved.sku
            setPiece({ sku: resolved.sku, title: resolved.title })
        }
        const response = await reopenPiece(fetchResource, code)
        setBusy(false)
        const next = outcomeOf(code, response)
        if (next.kind === 'reopened') onReopened(code)
        else setOutcome(next)
    }

    function handleChange(e) {
        setBarcode(e.target.value)
        setOutcome(null)
        setPiece(null)
    }

    return (
        <>
            <Sheet
                open={open}
                onClose={() => { if (!busy) onClose() }}
                title={t('photoReview.reopen.title')}
                actions={(
                    <ActionBar
                        cancel={{ label: t('photoReview.reopen.cancel'), onClick: onClose, disabled: busy }}
                        confirm={{
                            label: t('photoReview.reopen.confirm'),
                            onClick: () => submit(barcode),
                            disabled: busy || !canSubmit(barcode.trim()),
                            startIcon: busy ? <CircularProgress size={16} color='inherit' /> : null,
                        }}
                    />
                )}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant='body2' color='text.secondary'>{t('photoReview.reopen.body')}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                            label={t('photoReview.reopen.barcode')}
                            value={barcode}
                            onChange={handleChange}
                            onKeyDown={e => { if (e.key === 'Enter') submit(barcode) }}
                            inputProps={{ autoComplete: 'off', autoCapitalize: 'none', spellCheck: false }}
                            autoFocus
                            disabled={busy}
                            sx={{ flex: 1 }}
                        />
                        {isCameraAvailable() && (
                            <Button variant='outlined' color='inherit' startIcon={<QrCodeScannerIcon />} disabled={busy}
                                onClick={() => setShowScanner(true)} sx={{ minHeight: 56 }}>
                                {t('common.scan')}
                            </Button>
                        )}
                    </Box>
                    {piece && (
                        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                            <Typography sx={{ fontWeight: 600 }}>{piece.sku}</Typography>
                            {piece.title && <Typography variant='body2'>{piece.title}</Typography>}
                        </Box>
                    )}
                    {outcome && <Outcome outcome={outcome} t={t} language={i18n.language} onOpen={() => onReopened(outcome.sku)} />}
                </Box>
            </Sheet>
            {/* A sibling of the sheet, as on the upload page: the camera is its own layer. */}
            {showScanner && (
                <BarcodeScanner
                    open
                    onClose={() => setShowScanner(false)}
                    onScan={code => {
                        setShowScanner(false)
                        setBarcode(code)
                        submit(code)
                    }}
                    title={t('photoReview.reopen.title')}
                />
            )}
        </>
    )
}

function Outcome({ outcome, t, language, onOpen }) {
    const { kind, sku } = outcome
    if (kind === 'already') {
        return (
            <Alert severity='info' action={<Button color='inherit' size='small' onClick={onOpen}>{t('photoReview.reopen.openPiece')}</Button>}>
                {t('photoReview.reopen.already', { sku, how: republishLine(t, outcome.republish, language) })}
            </Alert>
        )
    }
    if (kind === 'unmatched') {
        return (
            <Alert severity='error'>
                <Typography variant='body2'>{t('photoReview.reopen.unmatched', { sku })}</Typography>
                <Box component='ul' sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                    {outcome.problems.map(problem => (
                        <li key={`${problem.slot}-${problem.reason}`}>{`${problem.slot ? slotDisplayName(t, problem.slot) : t('photoReview.reopen.otherPhoto')}: ${problem.reason}`}</li>
                    ))}
                </Box>
            </Alert>
        )
    }
    if (kind === 'unresolved') {
        return <Alert severity='error'>{t(`photoReview.reopen.unresolved.${outcome.reason}`, { title: outcome.title, barcodes: outcome.barcodes })}</Alert>
    }
    if (kind === 'notLive') return <Alert severity='warning'>{t('photoReview.reopen.notLive', { sku })}</Alert>
    if (kind === 'started') {
        return (
            <Alert severity='error'>
                <Typography variant='body2'>{t('photoReview.reopen.started', { sku })}</Typography>
                <Typography variant='body2' sx={{ mt: 0.5 }}>{outcome.detail}</Typography>
            </Alert>
        )
    }
    return <Alert severity='error'>{outcome.detail}</Alert>
}
